// "add wifi" / "remove wifi" for ONE device — the Message Box centre pane's two
// dialogs, moved here unchanged so the home page's ⋮ menu (DeviceCardMenu) gets
// the SAME behaviour and the same look (owner, 2026-10-08). Both shells' console
// and the card menu render these; there is no second copy.
//
//   Add     pick a network the device's LOCATION knows (its registered list +
//           whatever sibling devices reported to `getallwifi` this session), or
//           type a new one → `[<uno>]ADDWIFIssid=<ssid>pass=<pass>`, then the
//           network is registered on that location once the device is heard
//           again (core/wifi/registerWifi).
//   Remove  ask the device (`getallwifi`) what its chip holds, ✕ one →
//           `[<uno>]REMOVEWIFIssid=<ssid>pass=<the STORED pass>`.
//
// ⚠️ NO `#<userId>` ON EITHER VERB — on the ESP8266 it ends up inside the
// password (core/devices/commands.js). `[<uno>]` IS required: without it an
// ESP32 treats the command as a broadcast to every device on the topic.
// ⚠️ A SUCCESSFUL ADD OR REMOVE REBOOTS THE DEVICE.
// ⚠️ WHOSE LOCATION is a CHOICE (useLocationChoices): on the bench the device is
// at the office and the network it needs is the customer's. The saved list
// carries passwords, so only a location the user may see is listed or written.
//
// `sendAddressed(message)` lets the console publish through its own logged path;
// without it the device's `<uno>/device` gets `[<uno>]<message>`.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField,
  CircularProgress, List, ListItem, ListItemText, IconButton, Chip, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useApp } from '../../core/state/AppContext';
import {
  getLocationWifiList, getDeviceDetail, getDevicesByLocation,
} from '../../core/api/api';
import { buildAddWifiMqtt, buildRemoveWifiMqtt, parseWifiList } from '../../core/devices/commands';
import { publishAddressed } from '../../core/utils/deviceController';
import { getMessages } from '../../core/mqtt/mqttMonitor';
import { registerWhenDeviceOnline } from '../../core/wifi/registerWifi';
import useLocationChoices from '../../core/hooks/useLocationChoices';
import useDeviceWifiList from '../../core/hooks/useDeviceWifiList';
import LocationSelect from './LocationSelect';
import { FIT_XS } from './fitDialog';

const asArray = (res) => (res && Array.isArray(res.data) ? res.data : []);
const MONO = { fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 10, lineHeight: 1.4 };

const defaultSend = (uno) => (message) => publishAddressed(uno, message);

// Every network known for a location: its registered list + what OTHER devices
// there reported to `getallwifi` (already in the monitor buffer, so free).
// ⚠️ GetAllWifiNetworksByLocationId has hung on the server — so the result says
// whether that source answered (dbFailed), never "none" when it did not.
async function loadSavedWifi(locationId) {
  if (locationId == null) return { list: [], dbFailed: false };
  let dbFailed = false;
  const dbRows = await getLocationWifiList(locationId).then(asArray).catch(() => null);
  if (dbRows === null) dbFailed = true;
  const registered = (dbRows || [])
    .map((w) => ({ ssid: w.WIFISSID ?? '', pass: w.WIFIPASSWORD ?? '', from: 'location' }))
    .filter((w) => w.ssid);
  // ⚠️ Siblings from GetAllDevicesByLocationID, not the user's own list — the
  // device may belong to someone else.
  const siblingRes = await getDevicesByLocation(locationId).catch(() => null);
  const siblings = new Set(asArray(siblingRes).map((d) => String(d.UniqueNumber)));
  const seen = [];
  getMessages().forEach((m) => {
    if (!siblings.has(m.uno) || !m.topic.endsWith('/client')) return;
    parseWifiList(m.payload.replace(/^\[[^\]]*\]/, '')).forEach((w) => seen.push({ ...w, from: 'device' }));
  });
  const bySsid = new Map();
  [...registered, ...seen].forEach((w) => { if (w.ssid && !bySsid.has(w.ssid)) bySsid.set(w.ssid, w); });
  return { list: [...bySsid.values()], dbFailed };
}

/** Add Wi-Fi to one device. */
/**
 * `buildPayload(ssid, pass)` overrides the MQTT wording — BLE Test sends the
 * same credentials over an open Bluetooth link, where the payload is
 * `chooseWifiPayload`'s (no `[uno]` prefix, and the form depends on the
 * characters in the credentials). Everything else about this dialog is the
 * point of reusing it: the LOCATION choice, its permission gate, and the saved
 * list with the passwords already in it (owner, 2026-10-25 — "in ble test also
 * there should be addwifi same as we have in desktop").
 *
 * ⚠️ REGISTERING STILL WAITS FOR THE DEVICE TO BE HEARD ON MQTT. Over BLE an
 * `ok` means "saved", never "joined" — so the network is written to the location
 * only once the board actually turns up on `<uno>/client`, which is exactly what
 * registerWhenDeviceOnline waits for. A BLE confirmation must not stand in for
 * it, or a location collects a network that does not work.
 */
export function AddWifiDialog({
  open, uno, subtitle, onClose, sendAddressed, buildPayload,
}) {
  const { isAllowed, loading: choicesLoading } = useLocationChoices();
  const [ssid, setSsid] = useState('');
  const [pass, setPass] = useState('');
  const [locationId, setLocationId] = useState(undefined);   // undefined = resolving
  const [saved, setSaved] = useState(null);
  const [dbFailed, setDbFailed] = useState(false);

  // The device's own location is the default choice.
  useEffect(() => {
    if (!open || !uno) return undefined;
    let alive = true;
    setSsid(''); setPass(''); setSaved(null); setDbFailed(false); setLocationId(undefined);
    getDeviceDetail(uno).then((r) => {
      const d = asArray(r)[0];
      if (alive) setLocationId(d ? (d.LocationID ?? d.LocationId ?? null) : null);
    }).catch(() => { if (alive) setLocationId(null); });
    return () => { alive = false; };
  }, [open, uno]);

  useEffect(() => {
    if (!open || locationId === undefined || choicesLoading) return undefined;
    let alive = true;
    setSaved(null);
    setDbFailed(false);
    loadSavedWifi(isAllowed(locationId) ? locationId : null).then((r) => {
      if (!alive) return;
      setSaved(r.list);
      setDbFailed(r.dbFailed);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locationId, choicesLoading]);

  const doAdd = () => {
    if (!uno || !ssid) return;
    (sendAddressed || defaultSend(uno))((buildPayload || buildAddWifiMqtt)(ssid, pass));
    const registerAt = isAllowed(locationId) ? locationId : null;
    if (registerAt) registerWhenDeviceOnline(String(uno), registerAt, ssid, pass);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} {...FIT_XS}>
      <DialogTitle sx={{ pb: 1 }}>
        Add Wi-Fi to {uno}
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{subtitle}</Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        <LocationSelect
          value={isAllowed(locationId) ? locationId : null}
          onChange={(id) => setLocationId(id)}
          sx={{ mt: 0.5 }}
        />
        {saved === null ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        ) : saved.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {/* eslint-disable-next-line no-nested-ternary */}
            {locationId == null
              ? "Couldn't work out which location this device belongs to — pick one above."
              : dbFailed
                ? "The server's list of saved networks for this location did not respond, so only "
                  + 'networks seen on a device are shown. Run getallwifi on a device here to discover more.'
                : 'No Wi-Fi known for this location yet. Run getallwifi on a device here to discover '
                  + 'what it already has.'}
          </Typography>
        ) : (
          <List dense sx={{ maxHeight: 180, overflowY: 'auto', bgcolor: 'action.hover', borderRadius: 1 }}>
            {saved.map((w, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <ListItem key={`${w.ssid}-${i}`} disablePadding>
                <Button
                  fullWidth
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 2 }}
                  onClick={() => { setSsid(w.ssid); setPass(w.pass); }}
                >
                  <ListItemText
                    primary={w.ssid}
                    secondary={w.pass}
                    primaryTypographyProps={{ sx: { fontSize: 13, fontWeight: 600 } }}
                    secondaryTypographyProps={{ sx: MONO }}
                  />
                  <Chip size="small" label={w.from === 'device' ? 'on a device' : 'location'} sx={{ height: 16, fontSize: 9 }} />
                </Button>
              </ListItem>
            ))}
          </List>
        )}
        {dbFailed && saved && saved.length > 0 && (
          <Typography variant="caption" color="warning.main">
            Server list unavailable — showing only networks seen on a device.
          </Typography>
        )}
        <Divider>or enter a new one</Divider>
        <TextField size="small" label="SSID" value={ssid} onChange={(e) => setSsid(e.target.value)} />
        <TextField size="small" label="Password" value={pass} onChange={(e) => setPass(e.target.value)} />
        <Typography variant="caption" color="text.secondary">
          Sent verbatim — SSID and password are case-sensitive. The device restarts to join.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={doAdd} disabled={!ssid}>Send to device</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Remove Wi-Fi from one device — asks the chip, then ✕ one. */
export function RemoveWifiDialog({
  open, uno, onClose, sendAddressed,
}) {
  const { userId } = useApp();
  const chip = useDeviceWifiList(uno, userId, !!(open && uno));
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Wi-Fi on {uno}</DialogTitle>
      <DialogContent sx={{ minWidth: 320 }}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {chip.silent ? (
          <Typography variant="body2" color="warning.main">No answer from the device — it may be offline.</Typography>
        ) : chip.list === null ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Asking the device…</Typography>
          </Box>
        ) : chip.list.length === 0 ? (
          <Typography variant="body2">No Wi-Fi networks found on the device.</Typography>
        ) : (
          <List dense>
            {chip.list.map((w, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <ListItem key={`${w.ssid}-${i}`} secondaryAction={(
                <IconButton
                  edge="end" size="small" color="error"
                  onClick={() => { (sendAddressed || defaultSend(uno))(buildRemoveWifiMqtt(w.ssid, w.pass)); onClose(); }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              >
                <ListItemText primary={w.ssid} primaryTypographyProps={{ sx: MONO }} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
